import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContracts';
import { PACKAGE_NAMES, MAX_MATRIX_LEVELS, MATRIX_TRACK_LEVELS } from '../../utils/constants';

const TeamLevels = ({ userId }) => {
  const { coreStorage } = useContracts();
  const [pkg, setPkg] = useState(1);
  const [counts, setCounts] = useState(Array(MAX_MATRIX_LEVELS).fill(0));
  const [expanded, setExpanded] = useState(null);
  const [members, setMembers] = useState({});         // { level: [ids] }
  const [loadingLvl, setLoadingLvl] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);

  // Load all 15 level counts when userId or package changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!coreStorage?.read || !userId) {
        setCounts(Array(MAX_MATRIX_LEVELS).fill(0));
        return;
      }
      setLoadingAll(true);
      try {
        const c = await coreStorage.read.getMatrixLevelCountsAll(userId, pkg);
        if (!cancelled) setCounts(c.map((x) => Number(x)));
      } catch {
        if (!cancelled) setCounts(Array(MAX_MATRIX_LEVELS).fill(0));
      } finally {
        if (!cancelled) setLoadingAll(false);
      }
    };
    load();
    setExpanded(null);
    setMembers({});
    return () => { cancelled = true; };
  }, [coreStorage, userId, pkg]);

  const toggleLevel = async (lvl) => {
    if (lvl > MATRIX_TRACK_LEVELS) return;          // counts only
    if (expanded === lvl) {
      setExpanded(null);
      return;
    }
    setExpanded(lvl);
    if (members[lvl]) return;                       // already loaded

    setLoadingLvl(lvl);
    try {
      const ids = await coreStorage.read.getMatrixLevelMembers(userId, pkg, lvl);
      setMembers((prev) => ({ ...prev, [lvl]: ids.map((x) => x.toString()) }));
    } catch {
      setMembers((prev) => ({ ...prev, [lvl]: [] }));
    } finally {
      setLoadingLvl(null);
    }
  };

  return (
    <div className="card">
      <div className="card-title">Team Levels</div>

      <div className="pkg-tabs">
        {PACKAGE_NAMES.map((n, i) => (
          <button
            key={i}
            className={`pkg-tab ${pkg === i + 1 ? 'active' : ''}`}
            onClick={() => setPkg(i + 1)}
          >
            P{i + 1}
          </button>
        ))}
      </div>

      <div className="lvl-table">
        <div className="lvl-thead">
          <span>Level</span>
          <span>Filled / Capacity</span>
          <span>Members</span>
        </div>

        {counts.map((c, i) => {
          const lvl = i + 1;
          const cap = Math.pow(2, lvl);
          const complete = c >= cap;
          const expandable = lvl <= MATRIX_TRACK_LEVELS;
          const isOpen = expanded === lvl;
          const list = members[lvl] || [];

          return (
            <div key={lvl} className={`lvl-row ${complete ? 'complete' : ''} ${isOpen ? 'open' : ''}`}>
              <button
                className="lvl-row-head"
                onClick={() => toggleLevel(lvl)}
                disabled={!expandable}
              >
                <span className="lvl-name">L{lvl}</span>
                <span className="lvl-progress">
                  <span className="lvl-bar">
                    <span style={{ width: `${Math.min(100, (c / cap) * 100)}%` }} />
                  </span>
                  <span className="lvl-meta">{c} / {cap}</span>
                </span>
                <span className="lvl-count">{c}</span>
                {expandable && (
                  <span className={`lvl-chev ${isOpen ? 'open' : ''}`}>▾</span>
                )}
              </button>

              {expandable && isOpen && (
                <div className="lvl-body">
                  {loadingLvl === lvl ? (
                    <div className="lvl-empty">Loading members…</div>
                  ) : list.length === 0 ? (
                    <div className="lvl-empty">No members on this level yet.</div>
                  ) : (
                    <div className="lvl-chips">
                      {list.map((id) => (
                        <span key={id} className="lvl-chip">#{id}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {loadingAll && <div className="lvl-empty">Loading levels…</div>}
      </div>

      <div className="lvl-foot">
        Levels 1–{MATRIX_TRACK_LEVELS} show member IDs. Levels {MATRIX_TRACK_LEVELS + 1}–{MAX_MATRIX_LEVELS} show counts only.
      </div>
    </div>
  );
};

export default TeamLevels;
